import { LitElement, html, css, nothing, render } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';

import { GetTextColor, GetBorderColor } from '../../modules/ColorTools.js';
import './katapult-scroll.js';

import { GetEventCoords } from '../../modules/GetEventCoords.js';
import { Clamp } from '../../modules/Clamp.js';
import { KatapultIcon } from './katapult-icon.js';
import { Path } from '../../modules/Path.js';
import { when } from 'lit/directives/when.js';

export class KatapultDialog extends LitElement {
  static get ALERT_CONFIG() {
    return {
      dialog: {
        closeOnEscape: true,
        closeOnOutsideClick: true,
        color: 'var(--secondary-color)',
        maxWidth: 600,
        title: 'Alert'
      },
      button: {
        label: 'Dismiss',
        outlined: true
      },
      template: (config) => html`
        ${config.html ?? html`<div style="text-align: center; white-space: pre-wrap;">${config.text ?? ''}</div>`}
        <katapult-button slot="buttons" config="${JSON.stringify(config.button)}" dialog-dismiss></katapult-button>
      `
    };
  }

  static get CONFIRM_CONFIG() {
    return {
      dialog: {
        title: 'Confirm',
        color: 'var(--secondary-color)',
        closeOnEscape: true,
        maxWidth: 600
      },
      cancelButton: {
        label: 'Cancel',
        outlined: true
      },
      confirmButton: {
        label: 'Confirm'
      },
      template: (config) => html`
        ${config.html ?? html`<div style="text-align: center; white-space: pre-wrap;">${config.text ?? ''}</div>`}
        <katapult-button slot="buttons" config="${JSON.stringify(config.cancelButton)}" dialog-dismiss></katapult-button>
        <katapult-button slot="buttons" config="${JSON.stringify(config.confirmButton)}"></katapult-button>
      `
    };
  }

  static _extendConfig(a, b) {
    // Applies config b to a.
    const res = Path.copy(a);
    for (const key in b) {
      if (b[key] === undefined) continue;
      if (Path.isObject(b[key])) {
        for (const subkey in b[key]) {
          if (b[key][subkey] === undefined) continue;
          a[key] ??= {};
          a[key][subkey] = b[key][subkey];
        }
      } else {
        a[key] = b[key];
      }
    }
    return a;
  }

  /**
   * @typedef {object} KatapultDialogAlertConfig
   * @property {string} text - The text to display in the body of the dialog.
   * @property {string} [title='Alert'] - The title of the dialog.
   * @property {object} [dialog] - A configuration object for the dialog.
   * @property {string} [dialog.color='var(--secondary-color)'] - The color of the dialog.
   * @property {object} [button] - A configuration object for the button.
   */

  /**
   * Create a simple alert dialog
   * @param {string|KatapultDialogAlertConfig} textOrConfig - The text to display in the body of the dialog, or a configuration object.
   * @param {string} [title='Alert'] - The title of the dialog
   * @param {string} [color='var(--secondary-color)'] - The color of the dialog
   * @example KatapultDialog.alert('It is Wednesday, my dudes!')
   * @example KatapultDialog.alert('It is Wednesday, my dudes!', 'Wednesday Alert')
   * @example KatapultDialog.alert('It is Wednesday, my dudes!', 'Wednesday Alert', 'var(--paper-green-500)')
   * @example KatapultDialog.alert({ text: 'It is Wednesday, my dudes!', dialog: {title: 'Wednesday Alert', color: 'var(--paper-green-500)', icon: 'celebration'}, button: {label: 'I love Wednesday', icon: 'favorite'}})
   * @return {KatapultDialog}
   */
  static alert(textOrConfig, title, color) {
    // Get or build user supplied config.
    const _config = Path.isObject(textOrConfig) ? textOrConfig : { text: textOrConfig, dialog: { title, color } };
    // TODO: Remove support for legacy body property.
    if (_config.body) _config.text ??= _config.body;
    // Apply user supplied config options.
    const config = KatapultDialog._extendConfig(KatapultDialog.ALERT_CONFIG, _config);
    // Create and return the dialog.
    return KatapultDialog.open(config);
  }

  /**
   * @typedef {object} KatapultDialogConfirmConfig
   * @property {string} [text] - The text to display in the body of the dialog.
   * @property {string} [title='Confirm'] - The title of the dialog
   * @property {object} [dialog] - A configuration object for the dialog.
   * @property {string} [dialog.title='Confirm'] - The title of the dialog.
   * @property {string} [dialog.color='var(--secondary-color)'] - The color of the dialog.
   * @property {boolean} [dialog.closeOnEscape=true] - Whether the dialog should close when the Escape key is pressed.
   * @property {number} [dialog.maxWidth=600] - The maximum width of the dialog.
   * @property {string} [dialog.icon] - The icon to display in the dialog header.
   * @property {object} [cancelButton] - A configuration object for the cancel button.
   * @property {object} [confirmButton] - A configuration object for the confirm button.
   * @property {Function} [template] - A function which accepts `config` as a parameter and returns a rendered html string.
   */

  /**
   * Opens a simple confirm dialog.
   * The dialog has an additional `confirmed` promise property which resolves to `true` or `false` upon the dialog closing.
   * @param {string|KatapultDialogConfirmConfig} textOrConfig - The text to display in the body of the dialog, or a configuration object.
   * @param {string} [title='Confirm'] - The title of the dialog
   * @param {string} [color='var(--secondary-color)'] - The color of the dialog
   * @param {boolean} [dontCloseOnConfirm=false] - If true, the dialog will not close when the confirm button is clicked.
   * @example KatapultDialog.confirm('Is it Wednesday?')
   * @example KatapultDialog.confirm('Is it Wednesday?', 'Wednesday Check')
   * @example KatapultDialog.confirm('Is it Wednesday?', 'Wednesday Check', 'var(--paper-green-500)')
   * @example KatapultDialog.confirm({ text: 'Is it Wednesday?', dialog: {title: 'Wednesday Check', color: 'var(--paper-green-500)', icon: 'event'}, cancelButton: {label: 'Alas, it is not'}, confirmButton: {label: 'It is, my dude!', icon: 'celebration'}})
   * @example KatapultDialog.confirm({ text: 'Is it Wednesday?', dialog: {title: 'Wednesday Check', color: 'var(--paper-green-500)', icon: 'event'}, cancelButton: {label: 'Alas, it is not'}, confirmButton: {label: 'It is, my dude!', icon: 'celebration'}}).confirmed.then(res => KatapultDialog.alert(`It is ${res ? 'Wednesday' : 'not Wednesday'}, my dude!`, 'Wednesday Result', 'var(--paper-green-500)'))
   * @return {KatapultDialog}
   */
  static confirm(textOrConfig, title, color, dontCloseOnConfirm = false) {
    // Get or build user supplied config.
    const _config = Path.isObject(textOrConfig) ? textOrConfig : { text: textOrConfig, dialog: { title, color } };
    // TODO: Remove support for legacy body property.
    if (_config.body) _config.text ??= _config.body;
    // Apply user supplied config options.
    const config = KatapultDialog._extendConfig(KatapultDialog.CONFIRM_CONFIG, _config);
    // Propagate dialog color to confirm button, if unset.
    config.confirmButton.color ??= config.dialog.color;
    // Create the dialog.
    const dialog = KatapultDialog.open(config);
    // Set up a confirmed promise.
    let _confirmedResolve;
    dialog.confirmed = new Promise((resolve) => (_confirmedResolve = resolve));
    // Once the dialog begins to open, add a click callback to the confirm button which resolves the promise as true.
    dialog.openStart.then(() =>
      dialog.buttons[1].addEventListener('click', (e) => {
        _confirmedResolve(true);
        // Close the dialog after confirm since the confirm button is not a "dialog-dismiss" button
        if (!dontCloseOnConfirm) dialog.closeAsync();
      })
    );
    // Resolve the promise as false if any other case as the dialog begins to close.
    dialog.closeStart.then(() => _confirmedResolve(false));
    // Return the dialog to the caller.
    return dialog;
  }

  /**
   * Opens an anonymous dialog.
   * @param {Object} config - A configuration object.
   * @param {Function} config.template - A function which accepts `config` as a parameter and returns a rendered html string.
   * @param {Object} config.dialog - A configuration object for the dialog.
   * @return {KatapultDialog}
   */
  static open(config) {
    // Create the dialog.
    const dialog = new KatapultDialog();
    // Set properties on the dialog.
    dialog.config = config.dialog;
    // Set the dialog's content.
    render(config.template?.(config) ?? '', dialog);
    // Add the dialog to the body.
    document.body.appendChild(dialog);
    // Open the dialog.
    dialog.open();
    // Once the dialog has closed, remove it from the DOM.
    dialog.closeComplete.then(() => document.body.removeChild(dialog));
    // Return the newly created dialog.
    return dialog;
  }

  static properties = {
    _boundaryBounds: { type: Object, state: true },
    boundaryElement: { type: Element },
    _boundaryElement: { type: Element, state: true },
    boundaryPadding: { type: Number },
    _centerBounds: { type: Object, state: true },
    centerElement: { type: Element },
    _centerElement: { type: Element, state: true },
    closeButton: { type: Boolean },
    color: { type: String },
    config: { type: Object },
    debug: { type: Boolean, reflect: true },
    _dialogBounds: { type: Object, state: true },
    draggable: { type: Boolean, reflect: true },
    dragging: { type: Boolean, reflect: true },
    _dragPosition: { type: Object, state: true },
    height: { type: Number },
    icon: { type: String },
    iconConfig: { type: Object },
    _manualWidth: { type: Number },
    _manualHeight: { type: Number },
    maxWidth: { type: Number },
    maxHeight: { type: Number },
    minWidth: { type: Number },
    minHeight: { type: Number },
    modal: { type: Boolean },
    noHeader: { type: Boolean },
    closeOnEscape: { type: Boolean },
    closeOnOutsideClick: { type: Boolean },
    opened: { type: Boolean, reflect: true },
    persistManualPosition: { type: Boolean },
    pinnable: { type: Boolean },
    pinLocation: { type: String },
    autoPin: { type: Boolean },
    _isPinned: { type: Boolean, state: true },
    _pinnedLocation: { type: String, state: true },
    resizable: { type: Boolean, reflect: true },
    resizing: { type: Boolean, reflect: true },
    size: { type: Number },
    _size: { state: true },
    _slotFlags: { type: Object },
    subtitle: { type: String },
    _targetPosition: { type: Object, state: true },
    textColor: { type: String },
    title: { type: String },
    toggleBorder: { type: Boolean },
    transform: { type: String },
    _borderToggled: { type: Boolean, state: true },
    width: { type: Number },
    noResetPosition: { type: Boolean },
    onlyDragFromHandle: { type: Boolean, reflect: true }
  };

  static styles = css`
    :host {
      display: contents;
      font-family: Roboto;
      font-size: var(--katapult-dialog-font-size);
      transition: font-size 0.3s;
    }

    :host > * {
      z-index: 9999;
      box-sizing: border-box;
    }

    :host([debug]) dialog {
      outline: 2px solid var(--katapult-dialog-debug-color);
    }

    :host([dragging]) ::slotted(*),
    :host([resizing]) ::slotted(*) {
      pointer-events: none;
    }

    [hidden] {
      display: none !important;
    }

    dialog {
      /* Position */
      position: fixed;
      top: var(--katapult-dialog-top, 0);
      left: var(--katapult-dialog-left, 0);
      height: var(--katapult-dialog-height);
      width: var(--katapult-dialog-width);
      min-height: var(--katapult-dialog-min-height);
      min-width: var(--katapult-dialog-min-width);
      max-height: var(--katapult-dialog-max-height);
      max-width: var(--katapult-dialog-max-width);
      margin: unset;

      /* Visual */
      display: flex;
      visibility: hidden;
      flex-direction: column;
      border-radius: 24px;
      box-shadow: 0px 6px 12px rgba(0, 0, 0, 0.2);
      border: 2px solid var(--katapult-dialog-border-color);
      padding: unset;
      overflow: hidden;
      outline: none;

      /* Animation */
      transition:
        transform 0.3s,
        opacity 0.3s;
      transform: var(--katapult-dialog-transform, translateY(24px) scale(0.98));
      opacity: 0;
      pointer-events: none;
    }

    dialog.open {
      /* transition: transform 0.3s cubic-bezier(0.1, 1.3, 0.7, 1), opacity 0.3s; */
      transform: none;
      opacity: 1;
      pointer-events: auto;
    }

    dialog::backdrop {
      pointer-events: none;
      transition:
        opacity 0.3s,
        backdrop-filter 0.3s;
      opacity: 0;
      background: rgba(255, 255, 255, 0.5);
    }

    dialog.open::backdrop {
      opacity: 1;
      backdrop-filter: blur(4px);
    }

    #header {
      display: flex;
      justify-content: space-between;
      gap: 0.5em;
      background: var(--katapult-dialog-background);
      color: var(--katapult-dialog-color);
      border-bottom: 2px solid var(--katapult-dialog-border-color);
      padding: 0.25em;
      box-sizing: border-box;
    }

    :host([draggable]:not([onlyDragFromHandle])) #header,
    .katapult-dialog-drag-handle {
      cursor: grab;
    }

    #header .content {
      display: flex;
      align-items: center;
      gap: 0.5em;
      width: fit-content;
    }

    #headerCenter {
      justify-content: center;
      flex-grow: 1;
    }

    #headerRightWrapper,
    #headerLeftWrapper {
      display: flex;
      width: var(--katapult-dialog-header-side-width);
    }

    #headerRightWrapper {
      justify-content: flex-end;
    }
    #headerLeftWrapper {
      justify-content: flex-start;
    }

    #titleContainer {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.25em;
    }

    #title,
    #subtitle {
      display: flex;
    }

    #title {
      font-weight: 500;
      font-size: calc(var(--katapult-dialog-font-size) + 2pt);
    }

    #subtitle {
      font-weight: 300;
      font-size: calc(var(--katapult-dialog-font-size) - 1pt);
      opacity: 0.9;
    }

    #body {
      flex-grow: 1;
    }

    ::slotted([slot='body']) {
      width: -webkit-fill-available;
      max-width: -webkit-fill-available;
    }

    #buttons {
      display: flex;
      align-items: stretch;
      justify-content: var(--katapult-dialog-buttons-justify-content);
      gap: 0.5em;
      padding: 1em;
    }

    .resizeHandle {
      position: absolute;
      background: transparent;
      z-index: 10;
    }

    .resizeHandle[data-dir='nw'] {
      cursor: nwse-resize;
      left: 0;
      top: 0;
      width: 16px;
      height: 16px;
    }

    .resizeHandle[data-dir='ne'] {
      cursor: nesw-resize;
      right: 0;
      top: 0;
      width: 16px;
      height: 16px;
    }

    .resizeHandle[data-dir='sw'] {
      cursor: nesw-resize;
      left: 0;
      bottom: 0;
      width: 16px;
      height: 16px;
    }

    .resizeHandle[data-dir='se'] {
      cursor: nwse-resize;
      right: 0;
      bottom: 0;
      width: 16px;
      height: 16px;
    }

    #resizeIndicator {
      position: absolute;
      right: 0;
      bottom: 0;
      width: 20px;
      height: 20px;
      pointer-events: none;
      z-index: 10;
      display: flex;
      align-items: flex-end;
      justify-content: flex-end;
    }

    #resizeIndicator svg {
      display: block;
    }

    #resizeIndicator line {
      stroke: #bbb;
      stroke-width: 2;
    }

    /* Pin location specific styles */
    :host([data-pinned-location='top-left']) dialog {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      border-top-right-radius: 0;
    }

    :host([data-pinned-location='top-right']) dialog {
      border-top-right-radius: 0;
      border-top-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    :host([data-pinned-location='bottom-left']) dialog {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      border-top-left-radius: 0;
    }

    :host([data-pinned-location='bottom-right']) dialog {
      border-bottom-right-radius: 0;
      border-bottom-left-radius: 0;
      border-top-right-radius: 0;
    }

    :host([data-pinned-location='bottom-middle']) dialog {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    /* Border toggle styles */
    :host([data-border-toggled='true']) dialog {
      border-radius: 0 !important;
    }
  `;

  render() {
    return html`
      <!-- Dialog Element -->
      <dialog>
        ${when(
          !this.noHeader,
          () => html`
            <!-- Header -->
            <div id="header">
              <div id="headerLeftWrapper">
                <div id="headerLeft" class="content">
                  <slot name="header-left">
                    <katapult-collapse class="katapult-dialog-drag-handle" horizontal .opened=${this.draggable}>
                      <katapult-button
                        icon="${!this.noResetPosition && this._dragPosition ? 'lock_reset' : 'drag_indicator'}"
                        iconOnly
                        .color=${this.color}
                        .textColor=${this.textColor}
                        .size=${this._size}
                        .disabled=${this.noResetPosition || !this._dragPosition}
                        noBorder
                        noBackground
                        @click="${!this.noResetPosition ? this.resetPosition : undefined}"
                      ></katapult-button>
                    </katapult-collapse>
                  </slot>
                  ${when(
                    this.pinnable,
                    () => html`
                      <katapult-button
                        icon="push_pin"
                        iconOnly
                        .color=${this.color}
                        .textColor=${this.textColor}
                        .size=${this._size}
                        noBorder
                        noBackground
                        .iconConfig=${{ fill: this._isPinned }}
                        @click="${this._handlePinClick}"
                        title="${this._isPinned ? 'Unpin from ' + this._pinnedLocation : 'Pin to location'}"
                      ></katapult-button>
                    `
                  )}
                  <!-- This slot allows you to add a second button in the top left corner to the right of the first one -->
                  <slot name="header-left-end"></slot>
                </div>
              </div>
              <div id="headerCenter" class="content">
                <!-- Center Start Slot -->
                <slot name="header-center-start"></slot>
                ${this.icon
                  ? html` <katapult-icon
                      .icon=${this.icon}
                      .color=${this.textColor}
                      .size=${this._size}
                      .config=${this.iconConfig}
                    ></katapult-icon>`
                  : nothing}
                <!-- Title Container -->
                <div id="titleContainer">
                  <div id="title" ?hidden=${!this._slotFlags.title && !this.title}>
                    ${this.title ? html`<span style="text-align: center;">${this.title}</span>` : nothing}
                    <slot name="title" @slotchange="${this._slotChangeHandler}"></slot>
                  </div>
                  <div id="subtitle" ?hidden=${!this._slotFlags.subtitle && !this.subtitle}>
                    ${this.subtitle ? html`<span style="text-align: center;">${this.subtitle}</span>` : nothing}
                    <slot name="subtitle" @slotchange="${this._slotChangeHandler}"></slot>
                  </div>
                </div>
                <!-- Center End Slot -->
                <slot name="header-center-end"></slot>
              </div>
              <div id="headerRightWrapper">
                <div id="headerRight" class="content">
                  <!-- This slot allows you to add a second button in the top right corner to the left of the first one -->
                  <slot name="header-right-start"></slot>
                  ${when(
                    this.toggleBorder,
                    () => html`
                      <katapult-button
                        icon="capture"
                        iconOnly
                        .color=${this.color}
                        .textColor=${this.textColor}
                        .size=${this._size}
                        noBorder
                        noBackground
                        @click="${this._handleBorderToggle}"
                        .iconConfig=${{ fill: !this._borderToggled }}
                        title="${this._borderToggled ? 'Restore border radius' : 'Remove border radius'}"
                      ></katapult-button>
                    `
                  )}
                  <katapult-collapse horizontal .opened=${this.closeButton}>
                    <katapult-button
                      icon="close"
                      iconOnly
                      .color=${this.color}
                      .textColor=${this.textColor}
                      .size=${this._size}
                      noBorder
                      noBackground
                      @click="${this.close}"
                    ></katapult-button>
                  </katapult-collapse>
                  <slot name="header-right"></slot>
                </div>
              </div>
            </div>
          `
        )}
        <!-- Before Scroll Content -->
        <slot name="before-scroll"></slot>
        <!-- Body -->
        <slot name="before-body"></slot>
        <katapult-scroll id="body" ?hidden=${!this._slotFlags.default}>
          <slot @slotchange="${this._slotChangeHandler}"></slot>
        </katapult-scroll>
        <!-- After Scroll Content -->
        <slot name="after-scroll"></slot>
        <!-- Buttons -->
        <div id="buttons" ?hidden=${!this._slotFlags.buttons}>
          <slot name="buttons" @slotchange="${this._buttonSlotChanged}"></slot>
        </div>

        <!-- Resize Handles -->
        ${this.resizable
          ? html`
              ${['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'].map(
                (dir) => html`
                  <div
                    class="resizeHandle"
                    data-dir="${dir}"
                    style="${this._getResizeHandleStyle(dir)}"
                    @mousedown=${(e) => this._onResizeHandleMouseDown(e, dir)}
                    @touchstart=${(e) => this._onResizeHandleMouseDown(e, dir)}
                  ></div>
                `
              )}
            `
          : nothing}
      </dialog>

      <!-- Debugging Containers -->
      ${this.debug
        ? html`
            <!-- Boundary Container -->
            <div
              style=${styleMap({
                position: 'fixed',
                border: '2px dashed var(--katapult-dialog-debug-color)',
                opacity: 0.5,
                pointerEvents: 'none',
                top: `${this._boundaryBounds?.yMin}px`,
                left: `${this._boundaryBounds?.xMin}px`,
                height: `${this._boundaryBounds?.height}px`,
                width: `${this._boundaryBounds?.width}px`
              })}
            >
              <div
                style=${styleMap({
                  height: '100%',
                  width: '100%',
                  opacity: 0.5,
                  outline: `${this.boundaryPadding}px solid var(--katapult-dialog-debug-color)`
                })}
              ></div>
            </div>

            <!-- Center Container -->
            <div
              style=${styleMap({
                position: 'fixed',
                border: '2px solid var(--katapult-dialog-debug-color)',
                opacity: 0.5,
                background: `
            linear-gradient(to top left,
                rgba(0,0,0,0) 0%,
                rgba(0,0,0,0) calc(50% - 2px),
                var(--katapult-dialog-debug-color) 50%,
                rgba(0,0,0,0) calc(50% + 2px),
                rgba(0,0,0,0) 100%),
            linear-gradient(to top right,
                rgba(0,0,0,0) 0%,
                rgba(0,0,0,0) calc(50% - 2px),
                var(--katapult-dialog-debug-color) 50%,
                rgba(0,0,0,0) calc(50% + 2px),
                rgba(0,0,0,0) 100%);
            `,
                pointerEvents: 'none',
                top: `${this._centerBounds?.yMin}px`,
                left: `${this._centerBounds?.xMin}px`,
                height: `${this._centerBounds?.height}px`,
                width: `${this._centerBounds?.width}px`,
                boxSizing: 'border-box'
              })}
            ></div>

            <!-- Target Position Dot -->
            <katapult-icon
              icon="open_with"
              style=${styleMap({
                position: 'fixed',
                color: 'var(--katapult-dialog-debug-color)',
                outline: '2px solid var(--katapult-dialog-debug-color)',
                background: 'white',
                borderRadius: '100%',
                pointerEvents: 'none',
                top: `calc(${this._targetPosition?.y}px - 12px)`,
                left: `calc(${this._targetPosition?.x}px - 12px)`
              })}
            ></katapult-icon>
          `
        : nothing}
    `;
  }

  constructor() {
    super();

    // Prevent default tooltip from showing.
    this.removeAttribute('title');

    // Set property defaults.
    this.color = 'var(--secondary-color)';
    this.opened = this.draggable = false;
    this._centerElement = this._boundaryElement = window;
    this.boundaryPadding = 16;
    this.minWidth = 300;
    this.minHeight = 130;
    this.maxWidth = this.maxHeight = Infinity;
    this._transitionSpeed = 300;
    this._slotFlags = {};
    this._isPinned = false;
    this._borderToggled = false;
    this.closeOnEscape = false;
    this.closeOnOutsideClick = false;
    this.noResetPosition = false;
    this.onlyDragFromHandle = false;

    /** @type { Boolean } */
    this.noHeader = false;

    // Set up the confirmed property.
    /** @type {Promise<boolean>|null} */
    this.confirmed = null;

    // Initialize promises.
    this._openStartPromise = new Promise((x) => (this._openStartResolve = x));
    this._openCompletePromise = new Promise((x) => (this._openCompleteResolve = x));
    this._closeStartPromise = new Promise((x) => (this._closeStartResolve = x));
    this._closeCompletePromise = new Promise((x) => (this._closeCompleteResolve = x));

    this._keydownHandler = function (e) {
      if (e.key == 'Escape' && this.isActiveDialog) this.closeOnEscape ? this.closeAsync() : this.pulse();
    }.bind(this);

    this._mouseDownHandler = function (e) {
      e.stopPropagation();
      // Determine if the click originated from or within the drag handle.
      const composedPath = e.composedPath();
      const dragZone =
        (!this.onlyDragFromHandle && composedPath.some((x) => x == this._header)) ||
        (composedPath.includes(this) && composedPath.some((x) => x.classList?.contains('katapult-dialog-drag-handle')));
      // If click came from drag handle, begin to drag the dialog.
      if (this.draggable && dragZone) {
        // Call prevent default to keep from selecting text on the page as you drag the dialog.
        e.preventDefault();
        // Get coords of the mouse down event.
        const coords = GetEventCoords(e);
        // let rect = this._calcRect(this);
        // if (!this._dragStartBounds) this._set_dragStartBounds(this._calcBounds(rect));
        // Calc offset between mouse down coords and current dialog position.
        this._dragOffset = { x: this._targetPosition.x - coords.clientX, y: this._targetPosition.y - coords.clientY };
        document.body.style.cursor = 'grab';

        window.addEventListener('mousemove', this._mouseMoveHandler);
        window.addEventListener('touchmove', this._mouseMoveHandler);
      }
      // Store whether or not the event occurred on the dialog.
      this._mousedDownOnDialog = e.composedPath().some((x) => x == this._dialog);
      // Store the button the event occurred within if there is one.
      this._mouseDownButton = e.composedPath().find((x) => x.tagName?.includes('BUTTON'));
    }.bind(this);

    this._mouseUpHandler = function (e) {
      e.stopPropagation();
      // Remove mouse move handlers.
      window.removeEventListener('mousemove', this._mouseMoveHandler);
      window.removeEventListener('touchmove', this._mouseMoveHandler);
      document.body.style.cursor = '';
      this._dialog.style.pointerEvents = '';
      // Clear dragging flag.
      this.dragging = false;
      // See if the user clicked outside of the dialog (starting and ending off of the dialog).
      let mouseUpOnDialog = e.composedPath().some((x) => x == this._dialog);
      let outsideClick = !mouseUpOnDialog && !this._mousedDownOnDialog;
      if (outsideClick && this.isActiveDialog) this.closeOnOutsideClick ? this.closeAsync() : this.pulse();
      // See if the user clicked a button (starting and ending on the same button).
      let mouseUpButton = e.composedPath().find((x) => x.tagName?.includes('BUTTON'));
      let dialogButtonClick =
        this.contains(mouseUpButton) &&
        mouseUpButton == this._mouseDownButton &&
        ['dialog-dismiss', 'dialog-confirm'].some((attr) => mouseUpButton.hasAttribute(attr));
      // Close the dialog if an applicable button was pressed.
      if (dialogButtonClick) this.closeAsync();
    }.bind(this);

    this._mouseMoveHandler = function (e) {
      e.stopPropagation();
      // Get coords of the mouse move event.
      const coords = GetEventCoords(e);
      // Set drag position.
      this._dragPosition = { x: coords.clientX + this._dragOffset.x, y: coords.clientY + this._dragOffset.y };
      // Set dragging flag.
      this.dragging = true;
      // Unpin dialog when user starts dragging
      if (this._isPinned) {
        this._unpinDialog();
      }
      // Set manually positioned flag.
      // this
    }.bind(this);

    this._resizeHandler = function (e) {
      // Update center element bounds.
      this._centerBounds = this._calcBounds(this._calcRect(this._centerElement));
      // Update boundary element bounds.
      this._boundaryBounds = this._calcBounds(this._calcRect(this._boundaryElement), this.boundaryPadding);
    }.bind(this);

    // Choose a debug color.
    const debugColors = ['red', 'blue', 'green'];
    window._katapultDialogIndex ??= 0;
    this.style.setProperty('--katapult-dialog-debug-color', debugColors[window._katapultDialogIndex++ % debugColors.length]);
  }

  connectedCallback() {
    super.connectedCallback();

    this._paletteAppliedHandler = () => this._updateColors();

    // Update colors when app configuration reports that the palette has been applied.
    window.addEventListener('palette-applied', this._paletteAppliedHandler);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('palette-applied', this._paletteAppliedHandler);
    // Make sure that the dialog is cleaned up.
    this._cleanupDialog();
  }

  get buttons() {
    // Return all buttons that are not hidden (as determined by offsetParent != null);
    const slot = this.renderRoot.querySelector('slot[name="buttons"]');
    if (!slot) return [];
    const elements = slot?.assignedElements().filter((x) => x instanceof Element) ?? [];
    const visibleElements = elements.filter((x) => {
      const style = window.getComputedStyle(x);
      const isHidden = style.display == 'none';
      return !isHidden;
    });
    return visibleElements;
  }

  get isActiveDialog() {
    // Get list of all dismissable elements.
    const elements = Array.from(window._dismissElementStack);
    // Filter down to only the modal elements.
    const modalElements = elements.filter((x) => x.modal);
    // Prioritize modal elements above non-modal.
    return (modalElements.length ? modalElements : elements).slice(-1)[0] == this;
  }

  get openStart() {
    return this._openStartPromise;
  }

  get openComplete() {
    return this._openCompletePromise;
  }

  get closeStart() {
    return this._closeStartPromise;
  }

  get closeComplete() {
    return this._closeCompletePromise;
  }

  get position() {
    return structuredClone(this._targetPosition);
  }

  set position(coordinates) {
    if (!coordinates || !coordinates?.x || !coordinates?.y) {
      this._targetPosition = null;
      return;
    }

    this._targetPosition = coordinates;
    this.setPosition(coordinates);
  }

  firstUpdated() {
    // Store a reference to the dialog.
    this._dialog = this.renderRoot.querySelector('dialog');
    this._dialog.addEventListener('close', () => {
      // Don't allow the browser dialog to close if the Katapult Dialog still says it should be open.
      if (this.opened) this._dialog[this.modal ? 'showModal' : 'show']();
    });
    this._dialog.addEventListener('cancel', (e) => e.preventDefault());
    // Store a reference ot the header.
    this._header = this.renderRoot.querySelector('#header');
    this._headerLeft = this.renderRoot.querySelector('#headerLeft');
    this._headerRight = this.renderRoot.querySelector('#headerRight');
    this._headerLeftWrapper = this.renderRoot.querySelector('#headerLeftWrapper');
    this._headerRightWrapper = this.renderRoot.querySelector('#headerRightWrapper');
  }

  willUpdate(changedProperties) {
    // Update properties from config.
    if (changedProperties.has('config')) {
      // Allow config object to set any public property.
      for (const prop in KatapultDialog.properties) {
        // Skip any private properties.
        if (KatapultDialog.properties[prop].state || prop == 'config') continue;
        if (this.config?.[prop] != null || changedProperties.get('config')?.[prop] != null) this[prop] = this.config[prop];
      }
    }

    // Update size.
    this._size = KatapultIcon.calcSize(this.size);
    if (changedProperties.has('_size')) {
      this.style.setProperty('--katapult-dialog-size', `${this._size}px`);
      this.style.setProperty('--katapult-dialog-font-size', `${(Number(this._size) * 2) / 3}px`);
    }

    if (changedProperties.has('transform')) {
      this.style.setProperty('--katapult-dialog-transform', this.transform);
    }

    // Update colors.
    if (['color', 'textColor'].some((x) => changedProperties.has(x))) {
      this._updateColors();
    }

    // Evaluate external boundary and center element properties.
    ['boundaryElement', 'centerElement'].forEach((prop) => {
      if (changedProperties.has(prop)) {
        const val = this[prop];
        this[`_${prop}`] = val instanceof Element || val instanceof Window ? val : window;
      }
    });

    // Update target position.
    if (['_centerBounds', '_dragPosition'].some((x) => changedProperties.has(x))) {
      this._targetPosition = this._dragPosition ?? { x: this._centerBounds?.xMid, y: this._centerBounds?.yMid };
    }

    // Resize dialog.
    const resizeProperties = ['_targetPosition', '_dialogBounds', '_boundaryBounds', 'minHeight', 'minWidth', 'maxHeight', 'maxWidth'];
    if (resizeProperties.some((x) => changedProperties.has(x))) {
      if (!resizeProperties.every((prop) => this[prop] != null)) {
        if (this.debug) console.warn('Not all resize properties are defined.  Skipping resize...');
      } else {
        // Calc top and left dialog position and limit such that the dialog never shrinks beyond minWidth and minHeight.
        const halfDialogHeight = this._dialogBounds.height / 2;
        const halfDialogWidth = this._dialogBounds.width / 2;

        let top, left;

        // If dialog is pinned, bypass boundary constraints
        if (this._isPinned) {
          top = this._targetPosition.y - halfDialogHeight;
          left = this._targetPosition.x - halfDialogWidth;
        } else {
          // Apply normal boundary constraints for unpinned dialogs
          top = Clamp(this._targetPosition.y - halfDialogHeight, this._boundaryBounds.yMin, this._boundaryBounds.yMax - this.minHeight);
          left = Clamp(this._targetPosition.x - halfDialogWidth, this._boundaryBounds.xMin, this._boundaryBounds.xMax - this.minWidth);

          // Clamp drag position so it doesn't exceed maximum compression of the dialog.
          const halfMinHeight = this.minHeight / 2;
          const halfMinWidth = this.minWidth / 2;
          this._targetPosition = {
            y: Clamp(this._targetPosition.y, this._boundaryBounds.yMin + halfMinHeight, this._boundaryBounds.yMax - halfMinHeight),
            x: Clamp(this._targetPosition.x, this._boundaryBounds.xMin + halfMinWidth, this._boundaryBounds.xMax - halfMinWidth)
          };
        }

        // Calculate the maximum allowed height and width of the dialog.
        const maxHeight = this._isPinned
          ? this.maxHeight
          : Math.min(
              (this._targetPosition.y - this._boundaryBounds.yMin) * 2,
              (this._boundaryBounds.yMax - this._targetPosition.y) * 2,
              this.maxHeight
            );
        const maxWidth = this._isPinned
          ? this.maxWidth
          : Math.min(
              (this._targetPosition.x - this._boundaryBounds.xMin) * 2,
              (this._boundaryBounds.xMax - this._targetPosition.x) * 2,
              this.maxWidth
            );
        //Manual width/height logic
        let dialogWidth = this.width;
        let dialogHeight = this.height;
        if (this._manualWidth != null) {
          dialogWidth = Clamp(this._manualWidth, this.minWidth, maxWidth);
        }
        if (this._manualHeight != null) {
          dialogHeight = Clamp(this._manualHeight, this.minHeight, maxHeight);
        }

        // Set style properties.
        this.style.setProperty('--katapult-dialog-top', `${top}px`);
        this.style.setProperty('--katapult-dialog-left', `${left}px`);
        this.style.setProperty('--katapult-dialog-height', dialogHeight ? `${dialogHeight}px` : 'fit-content');
        this.style.setProperty('--katapult-dialog-width', dialogWidth ? `${dialogWidth}px` : 'fit-content');
        this.style.setProperty('--katapult-dialog-min-height', `${this.minHeight}px`);
        this.style.setProperty('--katapult-dialog-min-width', `${this.minWidth}px`);
        this.style.setProperty('--katapult-dialog-max-height', `${maxHeight}px`);
        this.style.setProperty('--katapult-dialog-max-width', `${maxWidth}px`);
      }
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('opened')) this.opened ? this._openStart() : this._closeStart();

    // Evaluate internal boundary and center element properties.
    ['_boundaryElement', '_centerElement'].forEach((prop) => {
      // Only react to changes if the dialog is open.
      if (changedProperties.has(prop) && this.opened) {
        // Stop watching the previous element.
        this._destroyResizeObserver(changedProperties.get(prop));
        // Begin watching the new element.
        this._createResizeObserver(this[prop]);
      }
    });

    // Update dialog class.
    this._dialog.classList[this.opened ? 'add' : 'remove']('open');
  }

  _buttonSlotChanged(e) {
    setTimeout(() => {
      this.style.setProperty('--katapult-dialog-buttons-justify-content', this.buttons.length > 1 ? 'space-between' : 'flex-end');
    });
    this._slotChangeHandler(e);
  }

  _calcBounds(rect, offset = 0) {
    if (rect == null) return;
    const xMin = rect.left + offset;
    const xMax = rect.left + rect.width - offset;
    const width = xMax - xMin;
    const xMid = xMin + width / 2;
    const yMin = rect.top + offset;
    const yMax = rect.top + rect.height - offset;
    const height = yMax - yMin;
    const yMid = yMin + height / 2;
    return { xMin, xMax, width, xMid, yMin, yMax, height, yMid };
  }

  _calcRect(elem) {
    if (elem instanceof Element) return elem.getBoundingClientRect();
    if (elem instanceof Window)
      return {
        bottom: window.innerHeight,
        height: window.innerHeight,
        left: 0,
        right: window.innerWidth,
        top: 0,
        width: window.innerWidth,
        x: 0,
        y: 0
      };
  }
  _getResizeHandleStyle(dir) {
    // Matches the reference code's handle style
    const base = 'position:absolute;z-index:10001;background:transparent;touch-action:none;';
    const styles = {
      nw: 'top:-6px;left:-6px;width:16px;height:16px;cursor:nw-resize;',
      ne: 'top:-6px;right:-6px;width:16px;height:16px;cursor:ne-resize;',
      sw: 'bottom:-6px;left:-6px;width:16px;height:16px;cursor:sw-resize;',
      se: 'bottom:-6px;right:-6px;width:16px;height:16px;cursor:se-resize;',
      n: 'top:-6px;left:16px;right:16px;height:12px;cursor:n-resize;',
      s: 'bottom:-6px;left:16px;right:16px;height:12px;cursor:s-resize;',
      e: 'top:16px;right:-6px;bottom:16px;width:12px;cursor:e-resize;',
      w: 'top:16px;left:-6px;bottom:16px;width:12px;cursor:w-resize;'
    };
    return base + styles[dir];
  }

  _cleanupDialog() {
    if (this._setup) {
      // Make the dialog invisible.
      // NOTE: !This is important! It prevent slotted content with `pointer-events: auto` from receiving click events after the dialog is closed.
      this._dialog.style.visibility = 'hidden';
      // If resizing, clean up listeners and state
      if (this.resizing) {
        this.resizing = false;
        document.body.style.userSelect = '';
        window.removeEventListener('mousemove', this._onResizeHandleMouseMove);
        window.removeEventListener('touchmove', this._onResizeHandleMouseMove);
        window.removeEventListener('mouseup', this._onResizeHandleMouseUp);
        window.removeEventListener('touchend', this._onResizeHandleMouseUp);
      }
      // Remove dialog from the dismiss element stack.
      window._dismissElementStack.delete(this);
      // Remove click event listener.
      window.removeEventListener('mousedown', this._mouseDownHandler);
      window.removeEventListener('touchstart', this._mouseDownHandler);
      window.removeEventListener('mouseup', this._mouseUpHandler);
      window.removeEventListener('touchend', this._mouseUpHandler);
      // Remove keydown listener.
      window.removeEventListener('keydown', this._keydownHandler);
      // Remove window resize listener.
      window.removeEventListener('resize', this._resizeHandler);
      // Remove element resize observers.
      this._destroyResizeObserver(this._dialog);
      this._destroyResizeObserver(this._headerLeft);
      this._destroyResizeObserver(this._headerRight);
      this._destroyResizeObserver(this._centerElement);
      this._destroyResizeObserver(this._boundaryElement);
      this._resizeObserver?.disconnect();
      this._resizeObserver = null;
      // Clear _setup flag.
      this._setup = false;
    }
  }

  close() {
    this.opened = false;
  }

  closeAsync() {
    // Note: We sometimes want the dialog to be closed asynchronously such as when we are closing due to a katapult-button[dialog-dismiss].
    // Closing asynchronously allows any click events attached to the button to run before we begin closing the dialog.
    setTimeout(() => this.close());
  }

  _closeComplete() {
    // Cleanup the dialog.
    this._cleanupDialog();

    // TODO: This should also be a function.
    // Close the dialog element.
    this._dialog.close();
    // Reset the dialog's position.
    if (!this.persistManualPosition) this.resetPosition(true);

    // Resolve close complete promise.
    this._closeCompleteResolve();
    // Create new close complete promise.
    this._closeCompletePromise = new Promise((x) => (this._closeCompleteResolve = x));
    // Dispatch closed event.
    this.dispatchEvent(new CustomEvent('closed'));
  }

  _closeStart() {
    // Cancel pending open complete timeout.
    if (this._openCompleteTimeout) window.clearTimeout(this._openCompleteTimeout);
    // Dispatch closing event.
    if (this.dispatchEvent(new CustomEvent('closing', { cancelable: true }))) {
      // Resolve open start promise.
      this._closeStartResolve();
      // Create new close start promise.
      this._closeStartPromise = new Promise((x) => (this._closeStartResolve = x));
      // Set timeout to resolve close complete.
      this._closeCompleteTimeout = setTimeout(() => this._closeComplete(), this._transitionSpeed);
    } else {
      this.open();
    }
  }

  _createResizeObserver(elem) {
    if (elem instanceof Element) {
      this._resizeObserver.observe(elem);
    }
  }

  _destroyResizeObserver(elem) {
    if (elem instanceof Element) {
      this._resizeObserver.unobserve(elem);
    }
  }

  _slotChangeHandler(e) {
    const slot = e.currentTarget;
    this._slotFlags[slot.getAttribute('name') ?? 'default'] = e.currentTarget.assignedElements().length > 0;
  }

  open() {
    this.opened = true;
  }

  _openComplete() {
    // Resolve open complete promise.
    this._openCompleteResolve();
    // Create new open complete promise.
    this._openCompletePromise = new Promise((x) => (this._openCompleteResolve = x));
    // Dispatch opened event.
    this.dispatchEvent(new CustomEvent('opened'));
  }

  _openStart() {
    // Setup the dialog.
    this._setupDialog();
    // Make the dialog visible.
    this._dialog.style.visibility = 'visible';
    // Ask the dialog to open.
    if (!this._dialog.open) this._dialog[this.modal ? 'showModal' : 'show']();
    // Cancel pending close complete timeout.
    if (this._closeCompleteTimeout) window.clearTimeout(this._closeCompleteTimeout);
    // Dispatch opening event.
    if (this.dispatchEvent(new CustomEvent('opening', { cancelable: true }))) {
      // Resolve open start promise.
      this._openStartResolve();
      // Create new open start promise.
      this._openStartPromise = new Promise((x) => (this._openStartResolve = x));
      // Set timeout to resolve open complete.
      this._openCompleteTimeout = setTimeout(() => this._openComplete(), this._transitionSpeed);
    } else {
      this.close();
    }
  }

  pulse() {
    this._dialog.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.02)' }, { transform: 'scale(1)' }], {
      duration: 100,
      easing: 'ease-out'
    });
  }

  _handlePinClick() {
    if (this._isPinned) {
      this._unpinDialog();
    } else {
      this._pinDialog();
    }
  }

  _handleBorderToggle() {
    this._borderToggled = !this._borderToggled;
    this.setAttribute('data-border-toggled', this._borderToggled.toString());
  }

  _pinDialog() {
    let targetLocation;

    // If auto pin is enabled, find the nearest pin location
    if (this.autoPin && this._targetPosition && this._boundaryBounds) {
      targetLocation = this._findNearestPinLocation();
    } else {
      // Use the specified pin location or default to bottom-middle
      targetLocation = this.pinLocation || 'bottom-middle';
    }

    if (targetLocation && targetLocation !== 'null') {
      this._isPinned = true;
      this._pinnedLocation = targetLocation;
      this.setAttribute('data-pinned-location', targetLocation);
      this._pinToLocation(targetLocation);
    }
  }

  _unpinDialog() {
    this._isPinned = false;
    this._pinnedLocation = null;
    this.removeAttribute('data-pinned-location');
  }

  _findNearestPinLocation() {
    if (!this._targetPosition || !this._boundaryBounds) return this.pinLocation || 'top-right';

    const { x, y } = this._targetPosition;
    const bounds = this._boundaryBounds;

    // Calculate distances to each corner/edge
    const locations = {
      'top-left': Math.sqrt(Math.pow(x - bounds.xMin, 2) + Math.pow(y - bounds.yMin, 2)),
      'top-right': Math.sqrt(Math.pow(x - bounds.xMax, 2) + Math.pow(y - bounds.yMin, 2)),
      'bottom-left': Math.sqrt(Math.pow(x - bounds.xMin, 2) + Math.pow(y - bounds.yMax, 2)),
      'bottom-right': Math.sqrt(Math.pow(x - bounds.xMax, 2) + Math.pow(y - bounds.yMax, 2)),
      'bottom-middle': Math.sqrt(Math.pow(x - bounds.xMid, 2) + Math.pow(y - bounds.yMax, 2))
    };

    // Find the location with minimum distance
    let nearestLocation = this.pinLocation || 'top-right';
    let minDistance = Infinity;

    for (const [location, distance] of Object.entries(locations)) {
      if (distance < minDistance) {
        minDistance = distance;
        nearestLocation = location;
      }
    }

    return nearestLocation;
  }

  async _pinToLocation(location) {
    if (!this._boundaryBounds) return;

    const bounds = this._boundaryBounds;

    this._dialogBounds = this._calcBounds(this._calcRect(this._dialog));

    // Calculate natural width and height based on content and constraints
    const naturalWidth = Math.max(this.minWidth, Math.min(this._manualWidth || this.width || this._dialogBounds?.width, this.maxWidth));
    const naturalHeight = Math.max(
      this.minHeight,
      Math.min(this._manualHeight || this.height || this._dialogBounds?.height, this.maxHeight)
    );

    const dialogHalfWidth = naturalWidth / 2;
    const dialogHalfHeight = naturalHeight / 2;
    let targetX, targetY;

    // Calculate true boundary positions (without padding constraints)
    const trueBounds = {
      xMin: bounds.xMin - this.boundaryPadding,
      xMax: bounds.xMax + this.boundaryPadding,
      yMin: bounds.yMin - this.boundaryPadding,
      yMax: bounds.yMax + this.boundaryPadding,
      xMid: bounds.xMin + bounds.width / 2
    };

    switch (location) {
      case 'top-left':
        targetX = trueBounds.xMin + dialogHalfWidth;
        targetY = trueBounds.yMin + dialogHalfHeight;
        break;
      case 'top-right':
        targetX = trueBounds.xMax - dialogHalfWidth;
        targetY = trueBounds.yMin + dialogHalfHeight;
        break;
      case 'bottom-left':
        targetX = trueBounds.xMin + dialogHalfWidth;
        targetY = trueBounds.yMax - dialogHalfHeight;
        break;
      case 'bottom-right':
        targetX = trueBounds.xMax - dialogHalfWidth;
        targetY = trueBounds.yMax - dialogHalfHeight;
        break;
      case 'bottom-middle':
        targetX = trueBounds.xMid;
        targetY = trueBounds.yMax - dialogHalfHeight;
        break;
      default:
        return;
    }

    // Set the target position directly since pinned dialogs bypass boundary constraints
    this._targetPosition = { x: targetX, y: targetY };
  }

  async resetPosition(immediate) {
    const transitionSpeed = immediate === true ? 0 : undefined;
    if (this._centerBounds) await this.setPosition({ y: this._centerBounds.yMid, x: this._centerBounds.xMid }, transitionSpeed);
    this._dragPosition = null;
    // Unpin when resetting position
    if (this._isPinned) {
      this._unpinDialog();
    }
  }

  async setPosition(targetPosition, transitionSpeed = this._transitionSpeed) {
    // Javascript animate the dialog from it's current position to the new position.
    // We use javascript because then any dimensional changes in the dialog animate nicely.
    // Capture the current dialog position.
    const startPosition = this._targetPosition;
    // Capture the current time.
    const startTime = performance.now();
    // Define a timing function for the animation.
    const timing = (t) => {
      // Emulate the ease-in-out function.
      // https://css-tricks.com/emulating-css-timing-functions-javascript
      return (Math.sin((t - 0.5) * Math.PI) + 1) / 2;
    };
    // Run the animation inside of a promise.
    return new Promise((resolve) => {
      // Define a step function to animate the dialog.
      const step = (timestamp) => {
        // Get percentage.
        const t = transitionSpeed > 0 ? Clamp((timestamp - startTime) / transitionSpeed, 0, 1) : 1;
        // Get transition progress.
        const p = timing(t);
        // Set the target position to the new intermediate position.
        this._targetPosition = {
          y: startPosition.y + (targetPosition.y - startPosition.y) * p,
          x: startPosition.x + (targetPosition.x - startPosition.x) * p
        };
        if (t < 1) requestAnimationFrame(step);
        else resolve();
      };
      // Begin the animation.
      requestAnimationFrame(step);
    });
  }

  _setupDialog() {
    if (!this._setup) {
      // Add dialog to the dismiss element stack.
      window._dismissElementStack ??= new Set();
      window._dismissElementStack.add(this);
      // Attach click event listener.
      window.addEventListener('mousedown', this._mouseDownHandler);
      window.addEventListener('touchstart', this._mouseDownHandler);
      window.addEventListener('mouseup', this._mouseUpHandler);
      window.addEventListener('touchend', this._mouseUpHandler);
      // Attach keydown listener.
      window.addEventListener('keydown', this._keydownHandler);
      // Attach window resize listener.
      window.addEventListener('resize', this._resizeHandler);
      // Setup resize observer.
      this._resizeObserver ??= new ResizeObserver((entries) => {
        // Loop through resized elements.
        for (const entry of entries) {
          // Get the element.
          const elem = entry.target;
          // Set dialog bounds.
          if (elem == this._dialog) {
            this._dialogBounds = this._calcBounds(this._calcRect(elem));
          }
          // Set center bounds.
          if (elem == this._centerElement) {
            this._centerBounds = this._calcBounds(this._calcRect(elem));
          }
          // Set boundary bounds.
          if (elem == this._boundaryElement) {
            this._boundaryBounds = this._calcBounds(this._calcRect(elem), this.boundaryPadding);
          }
          if (elem == this._headerLeft || elem == this._headerRight) {
            this.style.setProperty(
              '--katapult-dialog-header-side-width',
              `${Math.max(this._headerLeft.offsetWidth, this._headerRight.offsetWidth)}px`
            );
          }
        }
      });
      // Attach element resize observers.
      this._createResizeObserver(this._dialog);
      this._createResizeObserver(this._headerLeft);
      this._createResizeObserver(this._headerRight);
      this._createResizeObserver(this._centerElement);
      this._createResizeObserver(this._boundaryElement);
      // Manually call the resize handler to initialize bounds (since it will only be called on change).
      this._resizeHandler();
      // Set _setup flag.
      this._setup = true;
    }
  }

  _updateColors() {
    // Update properties.
    this.style.setProperty('--katapult-dialog-background', this.color);
    this.style.setProperty('--katapult-dialog-color', this.textColor ?? GetTextColor(this.color, this));
    this.style.setProperty('--katapult-dialog-border-color', GetBorderColor(this.color, this));
  }

  _onResizeHandleMouseDown(e, dir = 'se') {
    if (!this.resizable) return;
    const startEvent = e.touches ? e.touches[0] : e;
    e.preventDefault();
    e.stopPropagation();
    this._resizeDir = dir;
    this._resizeStartX = startEvent.clientX;
    this._resizeStartY = startEvent.clientY;
    this._resizeStartWidth = this._manualWidth ?? this._dialogBounds?.width;
    this._resizeStartHeight = this._manualHeight ?? this._dialogBounds?.height;
    this._resizeStartLeft = this._dialogBounds.xMin;
    this._resizeStartTop = this._dialogBounds.yMin;
    this._resizeStartTargetX = this._targetPosition?.x;
    this._resizeStartTargetY = this._targetPosition?.y;
    this.resizing = true;
    document.body.style.cursor = window.getComputedStyle(e.target).cursor;
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', this._onResizeHandleMouseMove, { passive: false });
    window.addEventListener('touchmove', this._onResizeHandleMouseMove, { passive: false });
    window.addEventListener('mouseup', this._onResizeHandleMouseUp);
    window.addEventListener('touchend', this._onResizeHandleMouseUp);
  }

  _onResizeHandleMouseMove = (e) => {
    if (!this.resizing) return;
    const moveEvent = e.touches ? e.touches[0] : e;
    const clientX = moveEvent.clientX;
    const clientY = moveEvent.clientY;
    const dx = clientX - this._resizeStartX;
    const dy = clientY - this._resizeStartY;
    let newWidth = this._resizeStartWidth;
    let newHeight = this._resizeStartHeight;
    let newTargetX = this._resizeStartTargetX;
    let newTargetY = this._resizeStartTargetY;
    // Horizontal resizing
    if (this._resizeDir.includes('e')) {
      const potentialWidth = this._resizeStartWidth + dx;
      if (potentialWidth >= this.minWidth && potentialWidth <= this.maxWidth) {
        newWidth = potentialWidth;
        newTargetX = this._resizeStartTargetX + dx / 2;
      } else if (potentialWidth < this.minWidth) {
        newWidth = this.minWidth;
        newTargetX = this._resizeStartTargetX - (this._resizeStartWidth - this.minWidth) / 2;
      } else {
        newWidth = this.maxWidth;
        newTargetX = this._resizeStartTargetX + (this._resizeStartWidth - this.maxWidth) / 2;
      }
    }
    if (this._resizeDir.includes('w')) {
      const potentialWidth = this._resizeStartWidth - dx;
      if (potentialWidth >= this.minWidth && potentialWidth <= this.maxWidth) {
        newWidth = potentialWidth;
        newTargetX = this._resizeStartTargetX + dx / 2;
      } else if (potentialWidth < this.minWidth) {
        newWidth = this.minWidth;
        newTargetX = this._resizeStartTargetX + (this._resizeStartWidth - this.minWidth) / 2;
      } else {
        newWidth = this.maxWidth;
        newTargetX = this._resizeStartTargetX + (this._resizeStartWidth - this.maxWidth) / 2;
      }
    }
    // Vertical resizing
    if (this._resizeDir.includes('s')) {
      const potentialHeight = this._resizeStartHeight + dy;
      if (potentialHeight >= this.minHeight && potentialHeight <= this.maxHeight) {
        newHeight = potentialHeight;
        newTargetY = this._resizeStartTargetY + dy / 2;
      } else if (potentialHeight < this.minHeight) {
        newHeight = this.minHeight;
        newTargetY = this._resizeStartTargetY - (this._resizeStartHeight - this.minHeight) / 2;
      } else {
        newHeight = this.maxHeight;
        newTargetY = this._resizeStartTargetY + (this._resizeStartHeight - this.maxHeight) / 2;
      }
    }
    if (this._resizeDir.includes('n')) {
      const potentialHeight = this._resizeStartHeight - dy;
      if (potentialHeight >= this.minHeight && potentialHeight <= this.maxHeight) {
        newHeight = potentialHeight;
        newTargetY = this._resizeStartTargetY + dy / 2;
      } else if (potentialHeight < this.minHeight) {
        newHeight = this.minHeight;
        newTargetY = this._resizeStartTargetY + (this._resizeStartHeight - this.minHeight) / 2;
      } else {
        newHeight = this.maxHeight;
        newTargetY = this._resizeStartTargetY + (this._resizeStartHeight - this.maxHeight) / 2;
      }
    }
    this._manualWidth = newWidth;
    this._manualHeight = newHeight;
    this._targetPosition = { x: newTargetX, y: newTargetY };
    this.requestUpdate();
  };

  _onResizeHandleMouseUp = (e) => {
    this.resizing = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    window.removeEventListener('mousemove', this._onResizeHandleMouseMove);
    window.removeEventListener('touchmove', this._onResizeHandleMouseMove);
    window.removeEventListener('mouseup', this._onResizeHandleMouseUp);
    window.removeEventListener('touchend', this._onResizeHandleMouseUp);
  };
}

customElements.define('katapult-dialog', KatapultDialog);
